import {
  CPTRawMetrics,
  CPTScoringResult,
  CPTZScores,
  CPTResult,
  Stimulus,
  StimulusResponse,
  StimulusResponseState,
  StimulusType,
  TestType,
} from "types/tests";

export type CPTSeverity = "expected" | "borderline" | "needMonitoring" | "inconclusive";

export type CPTIndexKey = "attentiveness" | "timeliness" | "impulsiveness" | "hyperReactivity";

const ALL_INDEX_KEYS: readonly CPTIndexKey[] = [
  "attentiveness",
  "timeliness",
  "impulsiveness",
  "hyperReactivity",
] as const;

export interface TranslatedText {
  key: string;
  params?: Record<string, string | number>;
}

export interface CPTIndexAssessment {
  key: CPTIndexKey;
  labelKey: string;
  rationaleKey: string;
  value: TranslatedText;
  context: TranslatedText | null;
  zScore: number | null;
  severity: CPTSeverity;
}

export interface CPTAssessment {
  testType: TestType | string;
  overallSeverity: Exclude<CPTSeverity, "inconclusive">;
  verdict: {
    color: "green" | "yellow" | "red";
    severity: Exclude<CPTSeverity, "inconclusive">;
    concernIndexKeys: CPTIndexKey[];
  };
  indices: Record<CPTIndexKey, CPTIndexAssessment>;
  rawMetrics: CPTRawMetrics | null;
  counts: {
    totalStimuli: number;
    totalTargets: number;
    totalNonTargets: number;
    totalDistractors: number;
    totalResponses: number;
  };
}

export const INDEX_LABEL_KEY: Record<CPTIndexKey, string> = {
  attentiveness: "extraTests.continuousPerformance.attentiveness",
  timeliness: "extraTests.continuousPerformance.timeliness",
  impulsiveness: "extraTests.continuousPerformance.impulsiveness",
  hyperReactivity: "extraTests.continuousPerformance.hyperReactivity",
};

const INDEX_RATIONALE_KEY: Record<CPTIndexKey, string> = {
  attentiveness: "extraTests.continuousPerformance.result.indices.attentiveness.rationale",
  timeliness: "extraTests.continuousPerformance.result.indices.timeliness.rationale",
  impulsiveness: "extraTests.continuousPerformance.result.indices.impulsiveness.rationale",
  hyperReactivity: "extraTests.continuousPerformance.result.indices.hyperReactivity.rationale",
};

export const SEVERITY_BADGE_KEY: Record<CPTSeverity, string> = {
  expected: "extraTests.continuousPerformance.result.severity.expected",
  borderline: "extraTests.continuousPerformance.result.severity.borderline",
  needMonitoring: "extraTests.continuousPerformance.result.severity.needMonitoring",
  inconclusive: "extraTests.continuousPerformance.result.severity.inconclusive",
};

export const VERDICT_HEADLINE_KEY: Record<Exclude<CPTSeverity, "inconclusive">, string> = {
  expected: "extraTests.continuousPerformance.result.verdict.expected.headline",
  borderline: "extraTests.continuousPerformance.result.verdict.borderline.headline",
  needMonitoring: "extraTests.continuousPerformance.result.verdict.needMonitoring.headline",
};

export const VERDICT_DESCRIPTION_KEY: Record<Exclude<CPTSeverity, "inconclusive">, string> = {
  expected: "extraTests.continuousPerformance.result.verdict.expected.description",
  borderline: "extraTests.continuousPerformance.result.verdict.borderline.description",
  needMonitoring: "extraTests.continuousPerformance.result.verdict.needMonitoring.description",
};

export type CPTIndexDirection = "higher-is-better" | "lower-is-better";

export const INDEX_RAW_DIRECTION: Record<CPTIndexKey, CPTIndexDirection> = {
  attentiveness: "higher-is-better",
  timeliness: "higher-is-better",
  impulsiveness: "lower-is-better",
  hyperReactivity: "lower-is-better",
};

export function toPerformanceZ(key: CPTIndexKey, rawZ: number | null): number | null {
  if (rawZ === null || !Number.isFinite(rawZ)) return null;
  return INDEX_RAW_DIRECTION[key] === "higher-is-better" ? rawZ : -rawZ;
}

function pickZScores(metrics: CPTScoringResult | undefined): CPTZScores | null {
  if (!metrics) return null;
  return metrics.parametricZScores ?? metrics.nonParametricZScores ?? null;
}

function severityForZ(z: number | null): CPTSeverity {
  if (z === null || !Number.isFinite(z)) return "inconclusive";
  if (z >= -1) return "expected";
  if (z > -2) return "borderline";
  return "needMonitoring";
}

const SEVERITY_RANK: Record<CPTSeverity, number> = {
  expected: 0,
  borderline: 1,
  needMonitoring: 2,
  inconclusive: -1,
};

function worstSeverity(items: CPTSeverity[]): Exclude<CPTSeverity, "inconclusive"> {
  let worst: Exclude<CPTSeverity, "inconclusive"> = "expected";
  for (const s of items) {
    if (s === "inconclusive") continue;
    if (SEVERITY_RANK[s] > SEVERITY_RANK[worst]) worst = s;
  }
  return worst;
}

function formatPercent(value: number | undefined | null, digits = 0): string {
  if (value === undefined || value === null || !Number.isFinite(value)) return "—";
  return `${value.toFixed(digits)}%`;
}

function formatCount(value: number | undefined | null): string {
  if (value === undefined || value === null || !Number.isFinite(value)) return "—";
  return `${Math.round(value)}`;
}

function indexValue(key: CPTIndexKey, raw: CPTRawMetrics | null | undefined): TranslatedText {
  if (!raw) return { key: "extraTests.continuousPerformance.result.noValue" };
  switch (key) {
    case "attentiveness":
      return {
        key: "extraTests.continuousPerformance.result.value.hitRate",
        params: { rate: formatPercent(raw.hitRate * 100, 2) },
      };
    case "timeliness":
      return {
        key: "extraTests.continuousPerformance.result.value.onTime",
        params: { rate: formatPercent(raw.timingRate * 100, 2) },
      };
    case "impulsiveness":
      return {
        key: "extraTests.continuousPerformance.result.value.falseAlarms",
        params: { rate: formatPercent(raw.falseAlarmRate * 100, 2) },
      };
    case "hyperReactivity": {
      const extraPresses = raw.multiPressCount;
      if (!Number.isFinite(extraPresses)) {
        return { key: "extraTests.continuousPerformance.result.noValue" };
      }
      return {
        key: "extraTests.continuousPerformance.result.value.extraPresses",
        params: { count: Math.round(extraPresses) },
      };
    }
  }
}

function indexContext(
  key: CPTIndexKey,
  raw: CPTRawMetrics | null | undefined,
): TranslatedText | null {
  if (!raw) return null;
  switch (key) {
    case "attentiveness":
      return {
        key: "extraTests.continuousPerformance.result.context.targetsOfTotal",
        params: { n: formatCount(raw.hits), total: formatCount(raw.nTargets) },
      };
    case "timeliness":
      return {
        key: "extraTests.continuousPerformance.result.context.targetsOfTotal",
        params: { n: formatCount(raw.timingCount), total: formatCount(raw.nTargets) },
      };
    case "impulsiveness":
      return {
        key: "extraTests.continuousPerformance.result.context.nonTargetsOfTotal",
        params: { n: formatCount(raw.falseAlarms), total: formatCount(raw.nNonTargets) },
      };
    case "hyperReactivity": {
      const targets = raw.nTargets;
      if (!Number.isFinite(targets)) return null;
      return {
        key: "extraTests.continuousPerformance.result.context.acrossTargets",
        params: { count: Math.round(targets) },
      };
    }
  }
}

function countCounts(testResult: CPTResult): CPTAssessment["counts"] {
  const stimuli: Stimulus[] = testResult.results?.stimuli ?? [];
  const responses: StimulusResponse[] = testResult.results?.responses ?? [];

  let targets = 0;
  let nonTargets = 0;
  let distractors = 0;
  for (const s of stimuli) {
    switch (s.type) {
      case StimulusType.Target:
        targets += 1;
        break;
      case StimulusType.NonTarget:
        nonTargets += 1;
        break;
      case StimulusType.VisualDistractor:
      case StimulusType.AudioDistractor:
      case StimulusType.AudioVisualDistractor:
        distractors += 1;
        break;
    }
  }

  return {
    totalStimuli: stimuli.length,
    totalTargets: targets,
    totalNonTargets: nonTargets,
    totalDistractors: distractors,
    totalResponses: responses.filter((r) => r.state !== StimulusResponseState.Miss).length,
  };
}

const VERDICT_COLOR: Record<Exclude<CPTSeverity, "inconclusive">, "green" | "yellow" | "red"> = {
  expected: "green",
  borderline: "yellow",
  needMonitoring: "red",
};

export function calculateCPTAssessment(testResult: CPTResult | undefined): CPTAssessment | null {
  if (!testResult || !testResult.results) return null;

  const metrics = testResult.results.metrics;
  const zScores = pickZScores(metrics);
  const rawMetrics = metrics?.rawMetrics ?? null;

  const indices = {} as Record<CPTIndexKey, CPTIndexAssessment>;
  for (const key of ALL_INDEX_KEYS) {
    const rawZ = zScores ? zScores[key] : null;
    const safeRawZ = typeof rawZ === "number" && Number.isFinite(rawZ) ? rawZ : null;
    const performanceZ = toPerformanceZ(key, safeRawZ);
    indices[key] = {
      key,
      labelKey: INDEX_LABEL_KEY[key],
      rationaleKey: INDEX_RATIONALE_KEY[key],
      value: indexValue(key, rawMetrics),
      context: indexContext(key, rawMetrics),
      zScore: safeRawZ,
      severity: severityForZ(performanceZ),
    };
  }

  const overallSeverity = worstSeverity(ALL_INDEX_KEYS.map((k) => indices[k].severity));
  const concernIndexKeys = ALL_INDEX_KEYS.filter(
    (k) => indices[k].severity === "borderline" || indices[k].severity === "needMonitoring",
  );
  const counts = countCounts(testResult);

  return {
    testType: testResult.testType,
    overallSeverity,
    verdict: {
      color: VERDICT_COLOR[overallSeverity],
      severity: overallSeverity,
      concernIndexKeys,
    },
    indices,
    rawMetrics,
    counts,
  };
}

export const SEVERITY_MANTINE_COLOR: Record<CPTSeverity, "green" | "yellow" | "red" | "gray"> = {
  expected: "green",
  borderline: "yellow",
  needMonitoring: "red",
  inconclusive: "gray",
};
